/**
 * Resolve KYC document URLs from a booking or unit-request record.
 */
export function resolveKycDocUrls(record) {
  if (!record) return { front: null, back: null, selfie: null };
  const requester = record.requester || record.requesterUser || record.user || null;
  const guest = record.guest || record.tenant || null;
  return {
    front:
      record.requesterKycIdDocumentUrl ||
      record.guestKycIdDocumentUrl ||
      record.userKycIdDocumentUrl ||
      requester?.kycIdDocumentUrl ||
      requester?.idDocumentUrl ||
      guest?.kycIdDocumentUrl ||
      guest?.idDocumentUrl ||
      null,
    back:
      record.requesterKycIdDocumentBackUrl ||
      record.guestKycIdDocumentBackUrl ||
      record.userKycIdDocumentBackUrl ||
      requester?.kycIdDocumentBackUrl ||
      requester?.idDocumentBackUrl ||
      guest?.kycIdDocumentBackUrl ||
      guest?.idDocumentBackUrl ||
      null,
    selfie:
      record.requesterKycSelfieUrl ||
      record.guestKycSelfieUrl ||
      record.userKycSelfieUrl ||
      requester?.kycSelfieUrl ||
      requester?.selfieUrl ||
      guest?.kycSelfieUrl ||
      guest?.selfieUrl ||
      null,
  };
}

function getPersonKeysForKyc(record, getUserId) {
  const userIdKey = String(getUserId(record) || '').trim();
  const emailKey = String(record?.requesterEmail || record?.guestEmail || '').trim().toLowerCase();
  const keys = [];
  if (userIdKey) keys.push(`uid:${userIdKey}`);
  if (emailKey) keys.push(`email:${emailKey}`);
  return keys;
}

function buildKycDocsByPersonKey(records, getUserId) {
  const docsByKey = new Map();
  (Array.isArray(records) ? records : []).forEach((record) => {
    const docs = resolveKycDocUrls(record);
    if (!docs.front && !docs.back && !docs.selfie) return;
    getPersonKeysForKyc(record, getUserId).forEach((key) => {
      const existing = docsByKey.get(key) || {};
      docsByKey.set(key, {
        front: existing.front || docs.front || null,
        back: existing.back || docs.back || null,
        selfie: existing.selfie || docs.selfie || null,
      });
    });
  });
  return docsByKey;
}

/**
 * Fill missing KYC URLs on displayed records using other records for the same person.
 */
export function applyKycGuestFallback(records, kycSources, getUserId) {
  if (!Array.isArray(records) || records.length === 0) return [];
  const docsByKey = buildKycDocsByPersonKey(
    [...records, ...(Array.isArray(kycSources) ? kycSources : [])],
    getUserId
  );

  return records.map((record) => {
    const currentDocs = resolveKycDocUrls(record);
    if (currentDocs.front || currentDocs.back || currentDocs.selfie) return record;

    const keys = getPersonKeysForKyc(record, getUserId);
    const fallbackDocs = keys.map((key) => docsByKey.get(key)).find(Boolean) || null;
    if (!fallbackDocs || (!fallbackDocs.front && !fallbackDocs.back && !fallbackDocs.selfie)) {
      return record;
    }

    return {
      ...record,
      requesterKycIdDocumentUrl: record.requesterKycIdDocumentUrl || fallbackDocs.front || null,
      requesterKycIdDocumentBackUrl: record.requesterKycIdDocumentBackUrl || fallbackDocs.back || null,
      requesterKycSelfieUrl: record.requesterKycSelfieUrl || fallbackDocs.selfie || null,
    };
  });
}
